import {
  CSSProperties,
  PointerEvent as ReactPointerEvent,
  RefObject,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

/** Позиция плавающей панели в координатах вьюпорта */
export type Position = { x: number; y: number };

/** Отступ панели от краёв вьюпорта при ограничении позиции */
const VIEWPORT_PADDING = 8;

/**
 * Запас справа и снизу, который остаётся у панели при открытии: её предельный размер задан
 * местом до края вьюпорта, поэтому панель, открытая вплотную к краю, растягиванию не поддаётся —
 * увеличить её удалось бы только после перетаскивания.
 */
const RESIZE_RESERVE = 64;

/** Обработчики указателя для элемента-ручки, за который тянут панель */
type DragHandlers = {
  onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerUp: (event: ReactPointerEvent<HTMLElement>) => void;
};

type DraggablePosition = {
  /** Ref на позиционируемую панель — нужен для ограничения позиции по её размерам */
  panelRef: RefObject<HTMLDivElement | null>;
  /** Позиция панели и ограничение её размера остатком вьюпорта */
  panelStyle: CSSProperties;
  dragHandlers: DragHandlers;
};

/**
 * Перетаскивание плавающей панели мышью. Панель всегда остаётся в пределах вьюпорта —
 * и когда её тянут за ручку, и когда её растягивают за угол: иначе крестик и угол
 * изменения размера уезжают за край экрана и панель становится неуправляемой.
 * Стартовая позиция уточняется по фактическим размерам панели после её отрисовки, с запасом
 * места на растягивание. Размер, заданный пользователем, при переносе сохраняется: панель
 * показывает ровно то, что он задал углом.
 */
export const useDraggablePosition = (initial: Position): DraggablePosition => {
  const panelRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<Position>(initial);
  // Ограничение размера включается только после того, как стартовая позиция сдвинута внутрь
  // вьюпорта: иначе панель, открытая у края, сжимается ещё до замера её собственного размера
  const [isPositionSettled, setIsPositionSettled] = useState(false);
  // Смещение точки захвата от левого верхнего угла панели; null — перетаскивания нет
  const dragOffsetRef = useRef<Position | null>(null);

  // reserve — дополнительное место, оставляемое справа и снизу от панели: нужно только при
  // открытии, при перетаскивании пользователь волен придвинуть панель вплотную к краю
  const clampToViewport = useCallback((next: Position, reserve = 0): Position => {
    const panel = panelRef.current;
    if (!panel) return next;

    const maxX = Math.max(window.innerWidth - panel.offsetWidth - VIEWPORT_PADDING - reserve, VIEWPORT_PADDING);
    const maxY = Math.max(window.innerHeight - panel.offsetHeight - VIEWPORT_PADDING - reserve, VIEWPORT_PADDING);

    return {
      x: Math.min(Math.max(next.x, VIEWPORT_PADDING), maxX),
      y: Math.min(Math.max(next.y, VIEWPORT_PADDING), maxY),
    };
  }, []);

  // Первый проход — до отрисовки и без ограничения размера: панель уже имеет свой штатный
  // размер, поэтому от края экрана она сдвигается целиком, а не ужимается в него
  useLayoutEffect(() => {
    setPosition((current) => clampToViewport(current, RESIZE_RESERVE));
    setIsPositionSettled(true);
  }, [clampToViewport]);

  const handlePointerDown = (event: ReactPointerEvent<HTMLElement>) => {
    // Тянем только левой кнопкой мыши
    if (event.button !== 0) return;

    // Размер, заданный углом, ограничен местом до края вьюпорта только на показ: у панели,
    // растянутой до упора в край, запомненный размер больше видимого, и вдали от края он бы
    // раскрылся — панель раздулась бы сама. Перед переносом закрепляем видимый размер как заданный;
    // дальше позиция ограничена по нему, поэтому в переносе панель уже не ужимается краем
    const panel = panelRef.current;
    if (panel) {
      panel.style.width = `${panel.offsetWidth}px`;
      panel.style.height = `${panel.offsetHeight}px`;
    }

    dragOffsetRef.current = { x: event.clientX - position.x, y: event.clientY - position.y };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLElement>) => {
    const dragOffset = dragOffsetRef.current;
    if (!dragOffset) return;

    setPosition(clampToViewport({ x: event.clientX - dragOffset.x, y: event.clientY - dragOffset.y }));
  };

  const handlePointerUp = (event: ReactPointerEvent<HTMLElement>) => {
    if (!dragOffsetRef.current) return;

    dragOffsetRef.current = null;
    event.currentTarget.releasePointerCapture(event.pointerId);
  };

  // Предельный размер задан от позиции панели: за угол её тянут вправо и вниз.
  // calc вместо замера окна — размер пересчитывается и при изменении размера окна
  const panelStyle: CSSProperties = {
    left: position.x,
    top: position.y,
    ...(isPositionSettled && {
      maxWidth: `calc(100vw - ${position.x + VIEWPORT_PADDING}px)`,
      maxHeight: `calc(100dvh - ${position.y + VIEWPORT_PADDING}px)`,
    }),
  };

  return {
    panelRef,
    panelStyle,
    dragHandlers: {
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerUp: handlePointerUp,
    },
  };
};
